import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import api from '../lib/api'

// Try backend first; if unavailable fall back to a direct Supabase profile query
// so auth works even when the backend API is not deployed.
async function fetchUserData(supabaseUser) {
  try {
    const { data } = await api.get('/auth/me')
    if (!data?.id || typeof data !== 'object') throw new Error('invalid')
    return data
  } catch {
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', supabaseUser.id)
      .maybeSingle()
    return {
      id: supabaseUser.id,
      email: supabaseUser.email,
      profile: profile ?? {},
      onboarding_complete: profile?.onboarding_complete ?? false,
    }
  }
}

export const useAuthStore = create((set, get) => ({
  user: null,
  profile: null,
  session: null,
  isLoading: true,
  isAuthenticated: false,

  loadUser: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        set({ user: null, profile: null, session: null, isAuthenticated: false, isLoading: false })
        return
      }
      const userData = await fetchUserData(session.user)
      set({ user: userData, profile: userData.profile, session, isAuthenticated: true, isLoading: false })
    } catch {
      set({ user: null, profile: null, session: null, isAuthenticated: false, isLoading: false })
    }
  },

  // Alias kept for existing callers
  checkAuth: () => get().loadUser(),

  login: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    const userData = await fetchUserData(data.user)
    set({ user: userData, profile: userData.profile, session: data.session, isAuthenticated: true })
    return userData
  },

  register: async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    })
    if (error) throw error
    set({ user: data.user, session: data.session, isAuthenticated: !!data.session })
    // Eagerly create the profile row so onboarding's PUT /profile always finds a row to upsert.
    if (data.session) {
      await get().loadUser()
    }
    return data
  },

  logout: async () => {
    await supabase.auth.signOut()
    set({ user: null, profile: null, session: null, isAuthenticated: false })
  },

  updateProfile: (profile) => {
    set({ profile, user: { ...get().user, profile } })
  },
}))

// Keep auth state in sync with Supabase session events
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    useAuthStore.setState({ user: null, profile: null, session: null, isAuthenticated: false })
  } else if (event === 'TOKEN_REFRESHED' && session) {
    useAuthStore.setState({ session })
  }
})
